import { resolveBrandingIconSet } from '@/lib/branding-icons'

import { siteConfig } from '@/constant/config'
import { normalizeHostname, resolveDomainBranding } from '@/utils/domain-branding-resolver'
import { normalizeTenantDisplayName } from '@/utils/tenant.utils'

interface RuntimeBrandingMetadataInput {
  hostname?: string
  tenantName?: string
  tenantLogo?: string
  tenantFaviconUrl?: string
}

export interface RuntimeBrandingMetadata {
  siteName: string
  authorName: string
  publisherName: string
  appTitle: string
  logoUrl: string
  icons: {
    ico: string
    png16: string
    png32: string
    appleTouchIcon: string
    manifest: string
  }
}

export function resolveRuntimeBrandingMetadata({
  hostname = '',
  tenantName = '',
  tenantLogo = '',
  tenantFaviconUrl = '',
}: RuntimeBrandingMetadataInput = {}): RuntimeBrandingMetadata {
  const resolvedBranding = resolveDomainBranding(hostname)

  const normalizedTenantName = normalizeTenantDisplayName(tenantName)
  const effectiveName = resolvedBranding.isFsdsDomain
    ? resolvedBranding.meta.siteName
    : normalizedTenantName || resolvedBranding.meta.siteName || resolvedBranding.brandName
  const effectivePublisher = resolvedBranding.isFsdsDomain
    ? resolvedBranding.meta.publisherName
    : normalizedTenantName || resolvedBranding.meta.publisherName || resolvedBranding.brandName
  const effectiveAppTitle = resolvedBranding.isFsdsDomain
    ? resolvedBranding.meta.appTitle
    : normalizedTenantName || resolvedBranding.meta.appTitle || resolvedBranding.brandName

  return {
    siteName: effectiveName,
    authorName: effectivePublisher,
    publisherName: effectivePublisher,
    appTitle: effectiveAppTitle,
    logoUrl: tenantLogo.trim() || resolvedBranding.logoUrl || resolvedBranding.icons.ico,
    icons: resolveBrandingIconSet(resolvedBranding.icons, tenantFaviconUrl),
  }
}

export function buildWebsiteSchema(input: RuntimeBrandingMetadataInput = {}) {
  const metadata = resolveRuntimeBrandingMetadata(input)
  const normalizedHostname = normalizeHostname(input.hostname)
  const siteUrl = normalizedHostname ? `https://${normalizedHostname}` : siteConfig.url

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: siteUrl,
    name: metadata.siteName,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
}

export const websiteBreadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://www.fullstackdatascience.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'MLE-1',
      item: 'https://www.fullstackdatascience.com/courses/mle-1',
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'MLE-2',
      item: 'https://www.fullstackdatascience.com/courses/mle-2',
    },
    {
      '@type': 'ListItem',
      position: 4,
      name: 'MLE-3',
      item: 'https://www.fullstackdatascience.com/courses/mle-3',
    },
    {
      '@type': 'ListItem',
      position: 5,
      name: 'MLE-4',
      item: 'https://www.fullstackdatascience.com/courses/mle-4',
    },
  ],
}
